import React, { useRef, useState, useCallback } from "react";
import {
    Alert, Autocomplete, Box, Button, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControl, FormControlLabel, InputLabel,
    MenuItem, Select, Switch, TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { checkPostalCode } from "../../../../services/postalService";
import useGetWayTypes from "../../../../hooks/Magasins/useGetWayTypes";
import type { EditForm } from "./ui";

interface EditCustomerDialogProps {
    open: boolean;
    onClose: () => void;
    fullName: string;
    initialValues: EditForm;
    onSave: (data: EditForm) => void;
    savingEdit: boolean;
    hasWaitingUpdate?: boolean;
    t: (key: string) => string;
}

const EditCustomerDialog = ({
    open, onClose, fullName, initialValues, onSave, savingEdit, hasWaitingUpdate, t,
}: EditCustomerDialogProps) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [titleId, setTitleId] = useState(initialValues.title_id);
    const [address3Type, setAddress3Type] = useState(initialValues.address3_type);
    const [reason, setReason] = useState("");
    const [mailCompany, setMailCompany] = useState(initialValues.mailCompany);
    const [emailCompany, setEmailCompany] = useState(initialValues.emailCompany);
    const [smsCompany, setSmsCompany] = useState(initialValues.smsCompany);
    const [mailTrusted, setMailTrusted] = useState(initialValues.mailTrusted);
    const [emailTrusted, setEmailTrusted] = useState(initialValues.emailTrusted);
    const [smsTrusted, setSmsTrusted] = useState(initialValues.smsTrusted);

    const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
    const [cityValue, setCityValue] = useState(initialValues.local_city_name);
    const [postalLoading, setPostalLoading] = useState(false);
    const zipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { data: wayTypes } = useGetWayTypes();

    const prevOpen = useRef(false);
    if (open && !prevOpen.current) {
        setTitleId(initialValues.title_id);
        setAddress3Type(initialValues.address3_type);
        setReason("");
        setMailCompany(initialValues.mailCompany);
        setEmailCompany(initialValues.emailCompany);
        setSmsCompany(initialValues.smsCompany);
        setMailTrusted(initialValues.mailTrusted);
        setEmailTrusted(initialValues.emailTrusted);
        setSmsTrusted(initialValues.smsTrusted);
        setCityValue(initialValues.local_city_name);
        setCitySuggestions([]);
    }
    prevOpen.current = open;

    const handleZipChange = useCallback((value: string) => {
        if (!/^\d{5}$/.test(value)) { setCitySuggestions([]); return; }
        if (zipTimer.current) clearTimeout(zipTimer.current);
        zipTimer.current = setTimeout(async () => {
            setPostalLoading(true);
            const cities = await checkPostalCode(value);
            setCitySuggestions(cities);
            setPostalLoading(false);
        }, 400);
    }, []);

    const handleSubmit = useCallback(() => {
        const fd = formRef.current ? new FormData(formRef.current) : new FormData();
        const str = (name: string) => (fd.get(name) as string) ?? "";
        onSave({
            title_id: titleId,
            lastname: str("lastname"),
            firstname: str("firstname"),
            birthdate: str("birthdate"),
            address1: str("address1"),
            address2: str("address2"),
            address3_num: str("address3_num"),
            address3_type: address3Type,
            address3_name: str("address3_name"),
            address4: str("address4"),
            zip_code: str("zip_code"),
            local_city_name: cityValue,
            local_country_name: str("local_country_name"),
            email_address: str("email_address"),
            cell_phone_number: str("cell_phone_number"),
            phone_number: str("phone_number"),
            mailCompany, emailCompany, smsCompany,
            mailTrusted, emailTrusted, smsTrusted,
            reason,
        });
    }, [titleId, address3Type, reason, mailCompany, emailCompany, smsCompany, mailTrusted, emailTrusted, smsTrusted, cityValue, onSave]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            PaperProps={{ sx: { maxHeight: "calc(100vh - 80px)" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
                <EditIcon fontSize="small" color="primary" />
                Modifier le client — {fullName}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {hasWaitingUpdate && (
                    <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
                        {t("pages.customers.dashboard.waitingUpdate.editBanner")}
                    </Alert>
                )}
                <form ref={formRef}>
                <Box sx={{ display: "flex", height: "100%" }}>
                    <Box sx={{ flex: "0 0 44%", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: "uppercase", letterSpacing: "0.6px" }}>
                            Informations personnelles
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <FormControl size="small" sx={{ width: 110, flexShrink: 0 }}>
                                <InputLabel>Civilité</InputLabel>
                                <Select value={titleId} label="Civilité"
                                    onChange={(e) => setTitleId(e.target.value)}>
                                    <MenuItem value="">—</MenuItem>
                                    <MenuItem value="1">M.</MenuItem>
                                    <MenuItem value="2">Mme</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField size="small" label="Prénom" name="firstname"
                                defaultValue={initialValues.firstname} sx={{ flex: 1 }} />
                            <TextField size="small" label="Nom" name="lastname"
                                defaultValue={initialValues.lastname} sx={{ flex: 1 }} />
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField size="small" label="Date de naissance" name="birthdate"
                                defaultValue={initialValues.birthdate}
                                type="date" sx={{ width: 180 }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => { (e.target as HTMLInputElement).showPicker?.(); } }} />
                        </Box>

                        <Divider />

                        <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: "uppercase", letterSpacing: "0.6px" }}>
                            Contact
                        </Typography>
                        <TextField fullWidth size="small" label="Email" type="email" name="email_address"
                            defaultValue={initialValues.email_address} />
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField size="small" label="Mobile" name="cell_phone_number"
                                defaultValue={initialValues.cell_phone_number} sx={{ flex: 1 }} />
                            <TextField size="small" label="Téléphone fixe" name="phone_number"
                                defaultValue={initialValues.phone_number} sx={{ flex: 1 }} />
                        </Box>

                        <Divider />

                        <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: "uppercase", letterSpacing: "0.6px" }}>
                            Autorisations CNIL
                        </Typography>
                        <Box sx={{ display: "flex", gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>Enseigne</Typography>
                                <FormControlLabel sx={{ display: "flex", mr: 0, mb: 0 }}
                                    control={<Switch size="small" checked={mailCompany} onChange={(e) => setMailCompany(e.target.checked)} />}
                                    label={<Typography variant="body2">Courrier</Typography>} />
                                <FormControlLabel sx={{ display: "flex", mr: 0, mb: 0 }}
                                    control={<Switch size="small" checked={emailCompany} onChange={(e) => setEmailCompany(e.target.checked)} />}
                                    label={<Typography variant="body2">Email</Typography>} />
                                <FormControlLabel sx={{ display: "flex", mr: 0, mb: 0 }}
                                    control={<Switch size="small" checked={smsCompany} onChange={(e) => setSmsCompany(e.target.checked)} />}
                                    label={<Typography variant="body2">SMS</Typography>} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>Tiers</Typography>
                                <FormControlLabel sx={{ display: "flex", mr: 0, mb: 0 }}
                                    control={<Switch size="small" checked={mailTrusted} onChange={(e) => setMailTrusted(e.target.checked)} />}
                                    label={<Typography variant="body2">Courrier</Typography>} />
                                <FormControlLabel sx={{ display: "flex", mr: 0, mb: 0 }}
                                    control={<Switch size="small" checked={emailTrusted} onChange={(e) => setEmailTrusted(e.target.checked)} />}
                                    label={<Typography variant="body2">Email</Typography>} />
                                <FormControlLabel sx={{ display: "flex", mr: 0, mb: 0 }}
                                    control={<Switch size="small" checked={smsTrusted} onChange={(e) => setSmsTrusted(e.target.checked)} />}
                                    label={<Typography variant="body2">SMS</Typography>} />
                            </Box>
                        </Box>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: "uppercase", letterSpacing: "0.6px" }}>
                            Adresse postale
                        </Typography>
                        <TextField fullWidth size="small" label="Adresse 1" name="address1"
                            defaultValue={initialValues.address1} inputProps={{ maxLength: 38 }} />
                        <TextField fullWidth size="small" label="Adresse 2" name="address2"
                            defaultValue={initialValues.address2} inputProps={{ maxLength: 38 }} />
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField size="small" label="N°" name="address3_num"
                                defaultValue={initialValues.address3_num} sx={{ width: 70 }}
                                inputProps={{ maxLength: 8 }} />
                            <FormControl size="small" sx={{ width: 110 }}>
                                <InputLabel>Type voie</InputLabel>
                                <Select
                                    value={address3Type}
                                    label="Type voie"
                                    onChange={(e) => setAddress3Type(e.target.value)}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {(wayTypes ?? []).map(wt => (
                                        <MenuItem key={wt.id} value={String(wt.id)}>{wt.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField size="small" label="Nom voie" name="address3_name"
                                defaultValue={initialValues.address3_name} sx={{ flex: 1 }}
                                inputProps={{ maxLength: 35 }} />
                        </Box>
                        <TextField fullWidth size="small" label="Adresse 4 (BP / Lieudit)" name="address4"
                            defaultValue={initialValues.address4} inputProps={{ maxLength: 38 }} />
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField size="small" label="Code postal" name="zip_code"
                                defaultValue={initialValues.zip_code} sx={{ width: 110 }}
                                inputProps={{ maxLength: 5 }}
                                InputProps={{ endAdornment: postalLoading ? <CircularProgress size={14} /> : undefined }}
                                onChange={(e) => handleZipChange(e.target.value)} />
                            <Autocomplete
                                freeSolo
                                options={citySuggestions}
                                value={cityValue}
                                onInputChange={(_, value) => setCityValue(value)}
                                sx={{ flex: 1 }}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" label="Ville"
                                        inputProps={{ ...params.inputProps, maxLength: 38 }} />
                                )}
                            />
                            <TextField size="small" label="Pays" name="local_country_name"
                                defaultValue={initialValues.local_country_name} sx={{ width: 100 }}
                                inputProps={{ maxLength: 38 }} />
                        </Box>
                    </Box>
                </Box>
                </form>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5, gap: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "grey.50", flexWrap: "wrap" }}>
                <Alert severity="warning" sx={{ flex: 1, fontSize: "0.78rem", py: 0.25, minWidth: 200 }}>
                    Toutes les modifications écraseront les données existantes. Elles seront effectives sous 24h.
                </Alert>
                <FormControl size="small" required error={!reason} sx={{ width: 230, flexShrink: 0 }}>
                    <InputLabel>Motif</InputLabel>
                    <Select
                        value={reason}
                        label="Motif"
                        onChange={(e) => setReason(e.target.value)}
                        MenuProps={{
                            disablePortal: true,
                            anchorOrigin: { vertical: "top", horizontal: "left" },
                            transformOrigin: { vertical: "bottom", horizontal: "left" },
                        }}
                    >
                        <MenuItem value="">— Sélectionner —</MenuItem>
                        <MenuItem value="COMPLEMENT D'INFORMATION">Complément d'information</MenuItem>
                        <MenuItem value="ERREUR DE SAISIE">Erreur de saisie</MenuItem>
                        <MenuItem value="DEMENAGEMENT">Déménagement</MenuItem>
                        <MenuItem value="CHGT DE NOM">Changement de nom</MenuItem>
                        <MenuItem value="CHGT DE PRENOM">Changement de prénom</MenuItem>
                        <MenuItem value="VALIDATION">Validation</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button onClick={onClose} color="inherit" disabled={savingEdit}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={savingEdit || !reason}
                        startIcon={savingEdit ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                    >
                        {savingEdit ? "Enregistrement…" : "Enregistrer"}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default EditCustomerDialog;
